import { useCallback, useEffect, useMemo, useState } from "react";
import toast from "../../utils/toast";
import { bookingsApi } from "../../services";
import "./bookingPanel.css";

// Định dạng Date -> value cho input datetime-local (YYYY-MM-DDTHH:mm theo giờ địa phương)
function toLocalInput(date) {
  const d = new Date(date);
  const off = d.getTimezoneOffset();
  const local = new Date(d.getTime() - off * 60000);
  return local.toISOString().slice(0, 16);
}

function formatDateTime(date) {
  if (!date) return "—";
  return new Date(date).toLocaleString("vi-VN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function BookingPanel({ post, username, onPostUpdated }) {
  const [bookings, setBookings] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    customerName: "",
    startAt: toLocalInput(new Date()),
    endAt: "",
    price: post?.price || "",
  });

  const isRented = post?.rentalStatus === "rented";

  const loadBookings = useCallback(async () => {
    if (!post?._id) return;
    try {
      const data = await bookingsApi.getAll({ postId: post._id });
      setBookings(Array.isArray(data) ? data : []);
    } catch (err) {
      // im lặng, không chặn trang
    }
  }, [post?._id]);

  useEffect(() => {
    loadBookings();
  }, [loadBookings]);

  const currentBooking = useMemo(
    () => bookings.find((b) => b.status === "active"),
    [bookings]
  );

  const handleField = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.customerName.trim()) return toast.error("Vui lòng nhập thông tin khách thuê");
    if (!form.startAt || !form.endAt) return toast.error("Vui lòng chọn thời gian bắt đầu và kết thúc");
    if (new Date(form.endAt) <= new Date(form.startAt)) {
      return toast.error("Thời gian kết thúc phải sau thời gian bắt đầu");
    }
    setSubmitting(true);
    try {
      await bookingsApi.create({
        postId: post._id,
        customerName: form.customerName.trim(),
        startAt: new Date(form.startAt).toISOString(),
        endAt: new Date(form.endAt).toISOString(),
        price: form.price,
        username,
      });
      toast.success("Đã tạo phiếu thuê — acc chuyển sang Đang cho thuê");
      onPostUpdated?.({ rentalStatus: "rented", rentedUntil: new Date(form.endAt).toISOString() });
      setShowForm(false);
      setForm((prev) => ({ ...prev, customerName: "", endAt: "" }));
      loadBookings();
    } catch (err) {
      toast.error("Tạo phiếu thuê thất bại");
    } finally {
      setSubmitting(false);
    }
  };

  const handleComplete = async (id) => {
    try {
      await bookingsApi.complete(id);
      toast.success("Đã kết thúc thuê — acc trở lại trạng thái trống");
      onPostUpdated?.({ rentalStatus: "available", rentedUntil: null });
      loadBookings();
    } catch (err) {
      toast.error("Thao tác thất bại");
    }
  };

  const handleCancel = async (id) => {
    try {
      await bookingsApi.cancel(id);
      toast.success("Đã hủy phiếu thuê");
      onPostUpdated?.({ rentalStatus: "available", rentedUntil: null });
      loadBookings();
    } catch (err) {
      toast.error("Thao tác thất bại");
    }
  };

  const handleExtend = async (booking) => {
    const input = window.prompt(
      "Gia hạn đến (định dạng: YYYY-MM-DD HH:mm)",
      toLocalInput(booking.endAt).replace("T", " ")
    );
    if (!input) return;
    const newEnd = new Date(input.replace(" ", "T"));
    if (isNaN(newEnd.getTime())) return toast.error("Thời gian không hợp lệ");
    try {
      await bookingsApi.update(booking._id, { endAt: newEnd.toISOString() });
      toast.success("Đã gia hạn phiếu thuê");
      onPostUpdated?.({ rentalStatus: "rented", rentedUntil: newEnd.toISOString() });
      loadBookings();
    } catch (err) {
      toast.error("Gia hạn thất bại");
    }
  };

  const history = bookings.filter((b) => b.status !== "active");

  return (
    <div className="booking-panel">
      <div className="booking-panel-header">
        <h3>
          <i className="fas fa-book"></i> Sổ thuê acc
        </h3>
        <span className={`rental-status-badge ${isRented ? "rented" : "available"}`}>
          {isRented ? "Đang cho thuê" : "Đang trống"}
        </span>
      </div>

      {/* Phiếu đang hoạt động */}
      {currentBooking ? (
        <div className={`current-booking ${currentBooking.isOverdue ? "overdue" : ""}`}>
          {currentBooking.isOverdue && (
            <div className="overdue-alert">
              <i className="fas fa-exclamation-triangle"></i> Đã quá hạn cho thuê!
            </div>
          )}
          <div className="booking-row"><span>Khách thuê</span><b>{currentBooking.customerName}{currentBooking.customerPhone ? ` - ${currentBooking.customerPhone}` : ""}</b></div>
          <div className="booking-row"><span>Bắt đầu</span><b>{formatDateTime(currentBooking.startAt)}</b></div>
          <div className="booking-row"><span>Hết hạn</span><b>{formatDateTime(currentBooking.endAt)}</b></div>
          <div className="booking-row"><span>Giá</span><b>{currentBooking.price || "—"}</b></div>
          {currentBooking.note && <div className="booking-row"><span>Ghi chú</span><b>{currentBooking.note}</b></div>}
          <div className="booking-actions">
            <button className="btn btn-success" onClick={() => handleComplete(currentBooking._id)}>
              <i className="fas fa-check"></i> Kết thúc thuê
            </button>
            <button className="btn btn-primary" onClick={() => handleExtend(currentBooking)}>
              <i className="fas fa-clock"></i> Gia hạn
            </button>
            <button className="btn btn-danger" onClick={() => handleCancel(currentBooking._id)}>
              <i className="fas fa-ban"></i> Hủy
            </button>
          </div>
        </div>
      ) : (
        <div className="no-current-booking">
          {showForm ? (
            <form className="booking-form" onSubmit={handleCreate}>
              <div className="form-grid">
                <label className="full-row">
                  Khách thuê <span className="req">*</span> <span className="label-hint">(tên, SĐT, ghi chú)</span>
                  <input name="customerName" value={form.customerName} onChange={handleField} placeholder="VD: Nguyễn Văn A - 0901xxxxxx - Zalo..." />
                </label>
                <label>
                  Bắt đầu <span className="req">*</span>
                  <input type="datetime-local" name="startAt" value={form.startAt} onChange={handleField} />
                </label>
                <label>
                  Hết hạn <span className="req">*</span>
                  <input type="datetime-local" name="endAt" value={form.endAt} onChange={handleField} />
                </label>
                <label>
                  Giá thuê
                  <input name="price" value={form.price} onChange={handleField} placeholder="VD: 3h/60000" />
                </label>
              </div>
              <div className="booking-actions">
                <button type="submit" className="btn btn-success" disabled={submitting}>
                  <i className="fas fa-check"></i> {submitting ? "Đang lưu..." : "Tạo phiếu thuê"}
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>
                  <i className="fas fa-times"></i> Hủy
                </button>
              </div>
            </form>
          ) : (
            <button className="btn btn-primary create-booking-btn" onClick={() => setShowForm(true)}>
              <i className="fas fa-plus"></i> Tạo phiếu thuê
            </button>
          )}
        </div>
      )}

      {/* Lịch sử thuê */}
      {history.length > 0 && (
        <div className="booking-history">
          <h4>Lịch sử thuê ({history.length})</h4>
          <ul>
            {history.map((b) => (
              <li key={b._id}>
                <span className={`hist-status ${b.status}`}>
                  {b.status === "completed" ? "Đã trả" : "Đã hủy"}
                </span>
                <span className="hist-name">{b.customerName}</span>
                <span className="hist-date">{formatDateTime(b.startAt)} → {formatDateTime(b.endAt)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
